import React, { useState, useEffect } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { CreditCard, X, Save, AlertTriangle, CheckCircle, Building2, Calendar } from 'lucide-react';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { validateClearingNumber, validateAccountNumber, getBankInfo } from '../../../../utils/bankAccountUtils';
import moment from 'moment';

// InputField component defined outside to prevent recreating on each render
const InputField = ({ icon: Icon, label, name, type = 'text', required = false, description, value, onChange, showValidation, errors, ...props }) => (
  <div className="input-group">
    <label className="input-label" htmlFor={`input-${name}`}>
      {label}
      {required && <span className="required-indicator">*</span>}
    </label>
    {description && <p className="input-description">{description}</p>}
    <div className="input-container">
      <div className="input-icon">
        <Icon size={16} />
      </div>
      <input
        id={`input-${name}`}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        data-testid={`input-${name}`}
        className={`modern-input ${showValidation && errors?.[name] ? 'error' : ''}`}
        {...props}
      />
    </div>
    {showValidation && errors?.[name] && (
      <div className="error-message">
        <AlertTriangle size={12} />
        <span>{errors[name]}</span>
      </div>
    )}
  </div>
);

const UpdateBankAccountModal = ({ show, handleClose, employeeId, companyId, currentBankAccount, currentContract, mode = 'create', onSuccess }) => {
  const isEditMode = mode === 'edit';
  const [formData, setFormData] = useState({
    clearing_no: '',
    account_no: '',
    iban: '',
    bic: '',
    valid_from: moment().format('YYYY-MM-DD')
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showValidation, setShowValidation] = useState(false);
  const [errors, setErrors] = useState({});
  const [bankInfo, setBankInfo] = useState(null);

  // Update form data when modal is shown
  useEffect(() => {
    if (show) {
      if (isEditMode && currentBankAccount) {
        // Pre-fill form with existing data when editing
        setFormData({
          clearing_no: currentBankAccount.clearing_no || '',
          account_no: currentBankAccount.account_no || '',
          iban: currentBankAccount.iban || '',
          bic: currentBankAccount.bic || '',
          valid_from: currentBankAccount.valid_from || moment().format('YYYY-MM-DD')
        });
        // Get bank info for existing account
        if (currentBankAccount.clearing_no) {
          const info = getBankInfo(currentBankAccount.clearing_no);
          setBankInfo(info);
        }
      } else {
        // Reset form for create mode
        // Use contract start date or today's date, whichever is later
        const today = moment().startOf('day');
        const contractStart = currentContract?.valid_from ? moment(currentContract.valid_from).startOf('day') : today;
        const defaultDate = moment.max(today, contractStart).format('YYYY-MM-DD');

        setFormData({
          clearing_no: '',
          account_no: '',
          iban: '',
          bic: '',
          valid_from: defaultDate
        });
        setBankInfo(null);
      }
      // Reset validation states when modal opens
      setShowValidation(false);
      setErrors({});
      setError(null);
    }
  }, [show, isEditMode, currentBankAccount, currentContract]);

  const validateForm = () => {
    const newErrors = {};

    // Validate clearing number
    if (!formData.clearing_no) {
      newErrors.clearing_no = 'Clearingnummer är obligatoriskt';
    } else {
      const clearingResult = validateClearingNumber(formData.clearing_no);
      if (!clearingResult.valid) {
        newErrors.clearing_no = clearingResult.message;
      }
    }

    // Validate account number
    if (!formData.account_no) {
      newErrors.account_no = 'Kontonummer är obligatoriskt';
    } else if (formData.clearing_no && !newErrors.clearing_no) {
      const accountResult = validateAccountNumber(formData.clearing_no, formData.account_no);
      if (!accountResult.valid) {
        newErrors.account_no = accountResult.message;
      }
    }

    // Validate valid_from date
    if (!formData.valid_from) {
      newErrors.valid_from = 'Datum måste anges';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Calculate minimum date for the date picker
  const getMinDate = () => {
    if (isEditMode) {
      return undefined; // No restriction when editing
    }

    const today = moment().startOf('day');
    const contractStart = currentContract?.valid_from ? moment(currentContract.valid_from).startOf('day') : today;

    // Use the later of today or contract start date
    return moment.max(today, contractStart).format('YYYY-MM-DD');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Update bank info when clearing number changes
    if (name === 'clearing_no') {
      if (value && value.length >= 4) {
        const info = getBankInfo(value);
        setBankInfo(info);
      } else {
        setBankInfo(null);
      }
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowValidation(true);

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const apiUrl = getApiUrl(`${companyId}/hr/employees/${employeeId}/bank-account`);

      const payload = {
        clearing_no: formData.clearing_no,
        account_no: formData.account_no,
        valid_from: formData.valid_from
      };

      if (formData.iban) payload.iban = formData.iban;
      if (formData.bic) payload.bic = formData.bic;

      await axios.put(apiUrl, payload, { withCredentials: true });

      if (onSuccess) onSuccess();
      resetAndClose();
    } catch (err) {
      console.error('Error updating bank account:', err);
      setError(err.response?.data?.message || 'Det gick inte att uppdatera bankkontot');
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setFormData({
      clearing_no: currentBankAccount?.clearing_no || '',
      account_no: currentBankAccount?.account_no || '',
      iban: currentBankAccount?.iban || '',
      bic: currentBankAccount?.bic || '',
      valid_from: isEditMode ? currentBankAccount?.valid_from : moment().format('YYYY-MM-DD')
    });
    setError(null);
    setShowValidation(false);
    setErrors({});
    setBankInfo(null);
    handleClose();
  };

  return (
    <>
      <style>{`
        .bank-modal-content {
          border-radius: 12px;
          overflow: hidden;
          border: none;
        }
        
        .bank-modal-header {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          padding: 1.5rem 2rem;
          border-bottom: 1px solid #e2e8f0;
          position: relative;
        }
        
        .bank-modal-body {
          padding: 2rem;
          background: #ffffff;
          max-height: 70vh;
          overflow-y: auto;
        }
        
        .close-button {
          position: absolute;
          top: 1rem;
          right: 1.5rem;
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          color: #64748b;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          cursor: pointer;
        }
        
        .close-button:hover {
          background: #e2e8f0;
          color: #475569;
        }
        
        .form-section {
          margin-bottom: 2rem;
        }
        
        .section-title {
          font-size: 1rem;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid #f1f5f9;
        }
        
        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
        }
        
        .input-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        
        .input-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          margin: 0;
        }
        
        .required-indicator {
          color: #ef4444;
          margin-left: 0.25rem;
        }

        .input-description {
          font-size: 0.75rem;
          color: #6b7280;
          margin: 0;
          line-height: 1.4;
        }
        
        .input-container {
          position: relative;
          display: flex;
          align-items: center;
        }
        
        .input-icon {
          position: absolute;
          left: 0.75rem;
          z-index: 1;
          color: #6b7280;
          pointer-events: none;
        }
        
        .modern-input {
          width: 100%;
          padding: 0.75rem 0.75rem 0.75rem 2.5rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.875rem;
          background: white;
          color: #374151;
          transition: all 0.2s ease;
        }
        
        .modern-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        
        .modern-input.error {
          border-color: #ef4444;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }
        
        .error-message {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #ef4444;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .bank-indicator {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 0.5rem;
          padding: 0.5rem 0.75rem;
          background: #ecfdf5;
          border: 1px solid #6ee7b7;
          border-radius: 6px;
          color: #047857;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .bank-comment {
          color: #059669;
          font-weight: 400;
          margin-left: 0.25rem;
        }

        .current-value-card {
          background: #dbeafe;
          border: 1px solid #60a5fa;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: start;
          gap: 0.75rem;
        }
        
        .current-value-icon {
          color: #2563eb;
          flex-shrink: 0;
          margin-top: 0.125rem;
        }
        
        .current-value-content {
          flex: 1;
        }
        
        .current-value-label {
          font-size: 0.75rem;
          color: #1e3a8a;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 0.25rem;
        }
        
        .current-value-text {
          font-size: 1rem;
          font-weight: 600;
          color: #1e40af;
          margin: 0;
        }
        
        .validation-summary {
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: start;
          gap: 0.75rem;
        }
        
        .validation-icon {
          color: #ef4444;
          flex-shrink: 0;
          margin-top: 0.125rem;
        }
        
        .validation-content {
          flex: 1;
        }
        
        .validation-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: #dc2626;
          margin-bottom: 0.5rem;
        }
        
        .modal-actions {
          background: #f8fafc;
          padding: 1.5rem 2rem;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .action-button {
          border-radius: 8px;
          padding: 0.75rem 1.5rem;
          font-weight: 600;
          font-size: 0.875rem;
          border: none;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
        }
        
        .btn-cancel {
          background: #f1f5f9;
          color: #64748b;
          border: 1px solid #e2e8f0;
        }
        
        .btn-cancel:hover {
          background: #e2e8f0;
          color: #475569;
        }
        
        .btn-save {
          background: var(--bs-primary);
          color: white;
        }
        
        .btn-save:hover {
          background: var(--bs-primary);
          opacity: 0.9;
        }
        
        .btn-save:disabled {
          background: #94a3b8;
          cursor: not-allowed;
        }
        
        .loading-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .form-info {
          background: #f0f9ff;
          border: 1px solid #bae6fd;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: start;
          gap: 0.75rem;
        }
        
        .info-icon {
          color: #0284c7;
          flex-shrink: 0;
          margin-top: 0.125rem;
        }
        
        .info-text {
          font-size: 0.875rem;
          color: #0c4a6e;
          margin: 0;
        }
        
        @media (max-width: 768px) {
          .form-grid {
            grid-template-columns: 1fr;
          }
          
          .modal-actions {
            flex-direction: column;
            gap: 1rem;
            align-items: stretch;
          }
          
          .action-button {
            justify-content: center;
          }
        }
      `}</style>

      <Modal show={show} onHide={resetAndClose} size="lg" backdrop="static" centered>
        <div className="bank-modal-content">
          {/* Header */}
          <div className="bank-modal-header">
            <button className="close-button" onClick={resetAndClose} aria-label="Stäng modal">
              <X size={16} />
            </button>
            <div className="d-flex align-items-center gap-3">
              <CreditCard size={24} color="#0891b2" />
              <div>
                <h3 className="mb-1" style={{ fontSize: '1.25rem', fontWeight: '600' }} data-testid="modal-title">
                  {isEditMode ? 'Redigera bankkonto' : 'Skapa nytt bankkonto'}
                </h3>
                <p className="mb-0" style={{ color: '#64748b', fontSize: '0.875rem' }}>
                  {isEditMode ? 'Uppdatera bankuppgifter för aktuellt konto' : 'Registrera bankuppgifter för löneutbetalning'}
                </p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="bank-modal-body">
            <Form onSubmit={handleSubmit} data-testid="form">
              {/* Error message */}
              {error && (
                <div className="validation-summary" data-testid="alert-danger">
                  <AlertTriangle size={16} className="validation-icon" />
                  <div className="validation-content">
                    <div className="validation-title">Ett fel uppstod</div>
                    <p style={{ fontSize: '0.875rem', color: '#dc2626', margin: 0 }}>{error}</p>
                  </div>
                </div>
              )}

              {/* Current value */}
              {currentBankAccount && (
                <div className="current-value-card">
                  <CreditCard size={20} className="current-value-icon" />
                  <div className="current-value-content">
                    <div className="current-value-label">Nuvarande konto</div>
                    <div className="current-value-text">
                      {currentBankAccount.clearing_no} - {currentBankAccount.account_no}
                    </div>
                  </div>
                </div>
              )}

              {/* Info message */}
              <div className="form-info" data-testid="alert-info">
                <CheckCircle size={16} className="info-icon" />
                <div className="info-text">
                  <strong>OBS:</strong> Clearingnummer och kontonummer är obligatoriska. IBAN och BIC behövs endast för internationella transaktioner.
                </div>
              </div>

              {/* Swedish Bank Account */}
              <div className="form-section">
                <div className="section-title">
                  <CreditCard size={18} />
                  Svenskt bankkonto
                </div>

                <div className="form-grid">
                  <div>
                    <InputField
                      icon={CreditCard}
                      label="Clearingnummer"
                      name="clearing_no"
                      type="text"
                      required={true}
                      placeholder="1234"
                      description="4-5 siffror som identifierar banken"
                      value={formData.clearing_no}
                      onChange={handleChange}
                      showValidation={showValidation}
                      errors={errors}
                      maxLength="5"
                    />
                    {bankInfo && formData.clearing_no && !errors.clearing_no && (
                      <div className="bank-indicator">
                        <Building2 size={14} />
                        <span>{bankInfo.bank}</span>
                        {bankInfo.comment && <span className="bank-comment">({bankInfo.comment})</span>}
                      </div>
                    )}
                  </div>

                  <InputField
                    icon={CreditCard}
                    label="Kontonummer"
                    name="account_no"
                    type="text"
                    required={true}
                    placeholder="12345678"
                    description="Kontonummer utan clearingnummer"
                    value={formData.account_no}
                    onChange={handleChange}
                    showValidation={showValidation}
                    errors={errors}
                    maxLength="45"
                  />
                </div>
              </div>

              {/* International Details */}
              <div className="form-section">
                <div className="section-title">
                  <CreditCard size={18} />
                  Internationella uppgifter (valfritt)
                </div>

                <div className="form-grid">
                  <InputField
                    icon={CreditCard}
                    label="IBAN"
                    name="iban"
                    type="text"
                    placeholder="SE1234567890123456789012"
                    description="Internationellt bankkontonummer"
                    value={formData.iban}
                    onChange={handleChange}
                    showValidation={showValidation}
                    errors={errors}
                    maxLength="34"
                  />

                  <InputField
                    icon={CreditCard}
                    label="BIC/SWIFT"
                    name="bic"
                    type="text"
                    placeholder="SWEDSESS"
                    description="Bankens identifieringskod för internationella transaktioner"
                    value={formData.bic}
                    onChange={handleChange}
                    showValidation={showValidation}
                    errors={errors}
                    maxLength="11"
                  />
                </div>
              </div>

              {/* Validity Period */}
              <div className="form-section">
                <div className="section-title">
                  <Calendar size={18} />
                  Giltighetstid
                </div>

                <div className="form-grid">
                  <InputField
                    icon={Calendar}
                    label="Gäller från"
                    name="valid_from"
                    type="date"
                    required={true}
                    description="Datum när bankkontot börjar gälla"
                    value={formData.valid_from}
                    onChange={handleChange}
                    showValidation={showValidation}
                    errors={errors}
                    disabled={isEditMode}
                    min={getMinDate()}
                  />
                </div>
              </div>
            </Form>
          </div>

          {/* Actions */}
          <div className="modal-actions">
            <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
              * Obligatoriska fält
            </div>
            <div className="d-flex gap-3">
              <button
                className="action-button btn-cancel"
                onClick={resetAndClose}
                disabled={loading}
                data-testid="button-cancel"
              >
                Avbryt
              </button>
              <button
                className="action-button btn-save"
                onClick={handleSubmit}
                disabled={loading}
                data-testid="button-primary"
              >
                {loading ? (
                  <>
                    <div className="loading-spinner"></div>
                    Sparar...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    {currentBankAccount ? 'Uppdatera bankkonto' : 'Lägg till bankkonto'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default UpdateBankAccountModal;
